import { Grid } from "@mui/material";
import { observer } from "mobx-react";
import React, { useCallback, useMemo } from "react";
import { ConversationClassifier } from "services/ConversationService";
import { useStore } from "utils/useStore";
import { AuthStore } from "stores/AuthStore";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import {
    getSliceCountForTooltipCutoff,
    hiddenTags,
    isFalseClassifierResult,
    ML_ContactType,
    ML_Hipaa,
    ML_PatientJourney,
    ML_PQC,
    safetyEventTags,
} from "./Helpers";
import ClassifierChip from "./ClassifierChip";
import { AdditionalClassifiersTooltip } from "./AdditionalClassifiersTooltip";

type Props = {
    conversationClassifiers: ConversationClassifier[];
    showAllChips?: boolean;
    selectedClassifierIds?: string[];
};

const chipMaxWidth = "200px";

const ConversationClassifierChipList: React.FunctionComponent<Props> = observer(
    ({ conversationClassifiers, showAllChips, selectedClassifierIds }) => {
        const authStore = useStore(AuthStore);
        const applicationFilterValuesStore = useStore(
            ApplicationFilterValuesStore,
        );

        const hideSafetyEvents = !authStore.canUserView("Safety Events Model");

        const hideHipaaCompliance = !authStore.canUserView(
            "HIPAA Compliance Model",
        );

        const hideTopicCluster = !authStore.canUserView("Topics");

        const hidePQC = !authStore.isUserUltra();

        const hidePatientJourney = !authStore.canUserView("Patient Journey");

        const filterHiddenClassifiers = useCallback(
            (item: ConversationClassifier) => {
                const shouldHideSafetyEvents =
                    hideSafetyEvents && safetyEventTags.includes(item.name);

                const shouldHideHipaaCompliance =
                    hideHipaaCompliance && item.name === ML_Hipaa;

                const shouldHidePQC = hidePQC && item.name === ML_PQC;

                const shouldHidePatientJourney =
                    hidePatientJourney && item.name === ML_PatientJourney;

                const showClassifier =
                    !shouldHidePatientJourney &&
                    !shouldHidePQC &&
                    !shouldHideSafetyEvents &&
                    !shouldHideHipaaCompliance &&
                    !isFalseClassifierResult(item) &&
                    !hiddenTags.includes(item.name);

                return showClassifier;
            },
            [
                hideSafetyEvents,
                hideHipaaCompliance,
                hidePQC,
                hidePatientJourney,
            ],
        );

        const { shownClassifiers, tooltipClassifiers } = useMemo(() => {
            const classifierList = [...conversationClassifiers]
                .filter(filterHiddenClassifiers)
                .sort((a, b) => {
                    // Contact type first
                    const isContactTypeOrder =
                        Number(b.name === ML_ContactType) -
                        Number(a.name === ML_ContactType);
                    if (isContactTypeOrder !== 0) return isContactTypeOrder;

                    // Tensorflow classifiers first
                    const aTensorflow = a.classifierType === "Tensorflow";
                    const bTensorflow = b.classifierType === "Tensorflow";
                    const tensorflowOrder =
                        Number(bTensorflow) - Number(aTensorflow);
                    if (tensorflowOrder !== 0) return tensorflowOrder;

                    // ML-PatientJourney first before RBCs
                    const aPatientJourney = a.name === ML_PatientJourney;
                    const bPatientJourney = b.name === ML_PatientJourney;
                    const patientJourneyOrder =
                        Number(aPatientJourney) - Number(bPatientJourney);
                    if (patientJourneyOrder !== 0) return patientJourneyOrder;

                    // Selected classifiers first
                    if (selectedClassifierIds?.length) {
                        const aSelected = selectedClassifierIds.includes(
                            a.classifierId,
                        );
                        const bSelected = selectedClassifierIds.includes(
                            b.classifierId,
                        );
                        const selectedOrder =
                            Number(bSelected) - Number(aSelected);
                        if (selectedOrder !== 0) return selectedOrder;
                    }

                    // Score descending
                    return b.score - a.score;
                });

            if (showAllChips) {
                return {
                    shownClassifiers: classifierList,
                    tooltipClassifiers: [],
                };
            }

            const sliceCount = getSliceCountForTooltipCutoff(
                classifierList,
                hideTopicCluster,
            );

            if (classifierList.length > sliceCount) {
                return {
                    shownClassifiers: classifierList.slice(0, sliceCount),
                    tooltipClassifiers: classifierList.slice(sliceCount),
                };
            }

            return {
                shownClassifiers: classifierList,
                tooltipClassifiers: [],
            };
        }, [
            filterHiddenClassifiers,
            conversationClassifiers,
            hideTopicCluster,
            selectedClassifierIds,
            showAllChips,
        ]);

        return (
            <Grid
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                    maxWidth: "none",
                }}
            >
                {shownClassifiers.map((item, index) => {
                    return (
                        <ClassifierChip
                            key={`${item.classifierId}-${item.result}-${index}`}
                            classifier={item}
                            index={index}
                            store={applicationFilterValuesStore}
                            chipMaxWidth={chipMaxWidth}
                        />
                    );
                })}
                {
                    <AdditionalClassifiersTooltip
                        tooltipClassifiers={tooltipClassifiers}
                        store={applicationFilterValuesStore}
                    />
                }
            </Grid>
        );
    },
);

export default ConversationClassifierChipList;
